package co.maytes.standupbot.dashboard.features.settings.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.SettingsField
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.Brand
import co.maytes.standupbot.dashboard.features.settings.BrandLogo
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSectionRow
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.features.theme.ThemeSegmented
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.auth.CurrentUserDto
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.integration.GithubAuthorizeUrlResponseDto
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.launch

@Composable
fun ProfileSection() {
    val auth = rememberAuthStore()
    val user = auth.currentUser

    SettingsPage(
        group = SettingsGroup.Account,
        title = "Profile",
        subtitle = user?.let {
            {
                SettingsSubtitle {
                    Strong(it.displayName)
                    Plain(" · ${roleLabel(it.role)} of ${it.workspace?.name ?: "your workspace"} · signed in with Slack")
                }
            }
        },
    ) {
        user?.let { IdentityRow(it) }
        ConnectedAccountsRow(user)
        AppearanceRow()
        SessionsRow(user)
        DangerRow(user)
    }
}

private fun roleLabel(role: RoleDto): String = when (role) {
    RoleDto.OWNER -> "Owner"
    RoleDto.ADMIN -> "Admin"
    RoleDto.MEMBER -> "Member"
    RoleDto.VIEWER -> "Viewer"
}

@Composable
private fun IdentityRow(user: CurrentUserDto) {
    SettingsSectionRow(
        anchor = "identity",
        title = "Identity",
        description = "How you appear on standups and in Slack.",
    ) {
        SettingsPanel {
            SettingsField(label = "Name", help = "From your Slack profile. Change it in Slack.") {
                ReadOnlyValue(user.displayName)
            }
            user.email?.let { email ->
                SettingsField(label = "Email", help = "From your Slack sign-in.") { ReadOnlyValue(email) }
            }
        }
    }
}

@Composable
private fun ReadOnlyValue(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.label.size)
            .fontWeight(400)
            .color(Palette.textPrimary),
    )
}

@Composable
private fun ConnectedAccountsRow(user: CurrentUserDto?) {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()
    var confirmOpen by remember { mutableStateOf(false) }
    var disconnecting by remember { mutableStateOf(false) }
    var connecting by remember { mutableStateOf(false) }
    val login = user?.githubLogin

    SettingsSectionRow(
        anchor = "connected-accounts",
        title = "Connected accounts",
        description = "Link developer accounts so StandupBot can attribute your activity.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = "GitHub",
                first = true,
                description =
                    if (login.isNullOrBlank()) {
                        "Link your pull requests to your profile."
                    } else {
                        "@$login"
                    },
                leading = { BrandLogo(Brand.GitHub, dimmed = login.isNullOrBlank()) },
                titleTrailing = {
                    if (login.isNullOrBlank()) {
                        Badge(label = "Not connected", tone = BadgeTone.Neutral)
                    } else {
                        Badge(label = "Connected", tone = BadgeTone.Success)
                    }
                },
                trailing = {
                    if (login.isNullOrBlank()) {
                        Button(
                            label = if (connecting) "Redirecting…" else "Connect GitHub",
                            variant = ButtonVariant.Primary,
                            size = ButtonSize.Sm,
                            enabled = !connecting,
                            onClick = {
                                if (connecting) return@Button
                                val wid = auth.currentUser?.workspace?.id ?: return@Button
                                scope.launch {
                                    connecting = true
                                    val res = api.get<GithubAuthorizeUrlResponseDto>(
                                        "/api/v1/workspaces/$wid/integrations/github/user/authorize-url",
                                    )
                                    when (res) {
                                        is Result.Ok -> window.location.href = res.value.url
                                        is Result.Err -> {
                                            connecting = false
                                            ui.showToast(
                                                "Couldn't start GitHub OAuth: ${res.error.displayMessage}",
                                                ToastKind.Error,
                                            )
                                        }
                                    }
                                }
                            },
                        )
                    } else {
                        Button(
                            label = if (disconnecting) "Disconnecting…" else "Disconnect",
                            variant = ButtonVariant.Secondary,
                            size = ButtonSize.Sm,
                            enabled = !disconnecting,
                            onClick = { confirmOpen = true },
                        )
                    }
                },
            )
        }
    }

    if (confirmOpen) {
        ConfirmModal(
            title = "Disconnect GitHub?",
            body = "StandupBot will stop including your GitHub activity in standups until you reconnect.",
            confirmLabel = "Disconnect",
            tone = ConfirmTone.Danger,
            onConfirm = {
                confirmOpen = false
                if (disconnecting) return@ConfirmModal
                scope.launch {
                    disconnecting = true
                    val res = api.delete<CurrentUserDto>("/api/v1/me/github")
                    disconnecting = false
                    when (res) {
                        is Result.Ok -> {
                            auth.bootstrap()
                            ui.showToast("GitHub disconnected", kind = ToastKind.Success)
                        }
                        is Result.Err ->
                            ui.showToast(
                                "Could not disconnect GitHub: ${res.error.displayMessage}",
                                kind = ToastKind.Error,
                            )
                    }
                }
            },
            onDismiss = { confirmOpen = false },
        )
    }
}

@Composable
private fun AppearanceRow() {
    SettingsSectionRow(
        anchor = "appearance",
        title = "Appearance",
        description = "Applies as soon as you pick one and stays on this device.",
    ) {
        SettingsPanel {
            SettingsField(label = "Theme", help = "System follows your computer.") { ThemeSegmented() }
        }
    }
}

@Composable
private fun SessionsRow(user: CurrentUserDto?) {
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    var signOutOpen by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }

    SettingsSectionRow(
        anchor = "sessions",
        title = "Sessions",
        description = "Where you are signed in.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = "Sign out everywhere",
                first = true,
                description = "Signs you out of every browser and device, including this one.",
                trailing = {
                    Button(
                        label = "Sign out everywhere",
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        enabled = !busy && user != null,
                        onClick = { signOutOpen = true },
                    )
                },
            )
        }
    }

    if (signOutOpen) {
        ConfirmModal(
            title = "Sign out everywhere?",
            body = "Every browser and device where you're signed in will be signed out, including this one. " +
                "Sign in again with Slack whenever you need to.",
            confirmLabel = "Sign out everywhere",
            onDismiss = { signOutOpen = false },
            onConfirm = {
                signOutOpen = false
                scope.launch {
                    busy = true
                    when (val result = auth.signOutEverywhere()) {
                        is Result.Ok -> {
                            ui.showToast("Signed out everywhere", ToastKind.Success)
                            ctx.router.navigateTo("/")
                        }
                        is Result.Err -> {
                            busy = false
                            ui.showToast("Couldn't sign out everywhere: ${result.error.displayMessage}", ToastKind.Error)
                        }
                    }
                }
            },
        )
    }
}

private const val ERASE_PHRASE = "erase my data"

@Composable
private fun DangerRow(user: CurrentUserDto?) {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    var eraseOpen by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    val isOwner = user?.role == RoleDto.OWNER

    SettingsSectionRow(
        anchor = "danger-zone",
        title = "Danger zone",
        description = "Cannot be undone.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = "Erase my personal data",
                first = true,
                description =
                    if (isOwner) {
                        "Owners can't erase themselves. Transfer ownership first (Settings, then Members)."
                    } else {
                        "Removes your name, email, avatar and linked accounts. Your past updates stay as \"Former member\"."
                    },
                trailing = {
                    Button(
                        label = "Erase my data",
                        variant = ButtonVariant.DangerOutline,
                        size = ButtonSize.Sm,
                        enabled = !busy && !isOwner && user != null,
                        onClick = { eraseOpen = true },
                    )
                },
            )
        }
    }

    if (eraseOpen) {
        ConfirmModal(
            title = "Erase your personal data?",
            body = "This permanently removes your name, email, avatar and linked Slack, Linear and GitHub accounts, " +
                "signs you out everywhere and removes you from every standup. Your past updates stay and show " +
                "\"Former member\". You can't undo this.",
            confirmLabel = "Erase my data",
            tone = ConfirmTone.Danger,
            confirmMatch = ERASE_PHRASE,
            onDismiss = { eraseOpen = false },
            onConfirm = {
                eraseOpen = false
                scope.launch {
                    busy = true
                    when (val result = api.delete<Unit>("/api/v1/me")) {
                        is Result.Ok -> {
                            auth.signOut()
                            ui.showToast("Your personal data was erased", ToastKind.Success)
                            ctx.router.navigateTo("/")
                        }
                        is Result.Err -> {
                            busy = false
                            ui.showToast("Couldn't erase your data: ${result.error.displayMessage}", ToastKind.Error)
                        }
                    }
                }
            },
        )
    }
}
